import { type ReactNode, useEffect, useLayoutEffect, useRef } from "react";
import { isString } from "../../../components";
import ProfilePhoto from "../../../components/ProfilePhoto";
import { DynamicInput } from "../../../components/input/DynamicInput";
import { useStatsigClient } from "../../../hooks";
import { SkillsBlock } from "../../dashboard/home/components/profile/SkillsBlock";
import { SkillSummary } from "../../dashboard/home/components/profile/SkillSummary";
import { ProfileSummaryContent } from "../../dashboard/home/components/link-creator";
import ProfileEducation from "../ProfileEducation";
import ProfileProject from "../ProfileProject";
import ProfileWorkplace from "../ProfileWorkplace";
import { ProfileBrandingHeader } from "./ProfileBrandingHeader";
import type { CVTemplate, CVTemplateProps } from "./types";

/**
 * Clip the left column's skill list to whole rows. The column is capped to one
 * A4 page (overflow-hidden), which would otherwise cut the last row mid-height.
 * Every chip is re-shown before measuring so the boundary (the column at its max
 * height) stays stable regardless of what's hidden; then any chip whose bottom
 * falls past the visible edge is hidden. The editor-only "Add skill" button is
 * hidden when the column is full so it only invites adding when there's room.
 */
const clipSkillRowsToColumn = (col: HTMLElement | null) => {
  if (!col) return;
  const chips = Array.from(
    col.querySelectorAll<HTMLElement>("[data-skill-chip]"),
  );
  const addBtn = col.querySelector<HTMLElement>("[data-add-skill]");
  for (const c of chips) c.style.display = "";
  if (addBtn) addBtn.style.display = "";
  const boundary = col.getBoundingClientRect().top + col.clientHeight;
  let clippedAny = false;
  for (const c of chips) {
    if (c.getBoundingClientRect().bottom > boundary + 0.5) {
      c.style.display = "none";
      clippedAny = true;
    }
  }
  if (addBtn) {
    const noRoom =
      clippedAny || addBtn.getBoundingClientRect().bottom > boundary + 0.5;
    if (noRoom) addBtn.style.display = "none";
  }
};

/**
 * SplitTemplate — a two-column *intro* on the first page (photo + skills on the
 * left, name/title/pitch/summary on the right), followed by a single full-width
 * body (experience/projects/education) that flows across pages. Step 5 of the
 * CV-template refactor (see /CV_TEMPLATES_PLAN.md).
 *
 * The intro is a self-contained row with break-inside: avoid, so pagination stays
 * simple — we deliberately avoid a sidebar that spans multiple A4 pages. Reuses
 * the same content child components and the same ProfileViewModel as the other
 * templates; only the arrangement differs.
 */
const SplitTemplate = ({
  vm,
  tokens,
  pdfMode,
  isEditable = false,
  logoUrl,
  showBranding,
  onEditPitch,
  onEditName,
  onEditTitle,
  onEditIntro,
  onEditHighlight,
  onEditWorkplace,
  onRemoveSkill,
  onAddStackSkill,
  onAddTopSkill,
  onRemoveTopSkill,
}: CVTemplateProps) => {
  const statsigClient = useStatsigClient();

  const skillColumnRef = useRef<HTMLDivElement>(null);

  // Re-clip synchronously on every render (skills/config changes), before paint —
  // this is also what the PDF export captures.
  useLayoutEffect(() => {
    clipSkillRowsToColumn(skillColumnRef.current);
  });

  // Re-clip on container/window resizes via one stable observer. The work is
  // deferred to the next frame so mutating chip visibility inside the callback
  // can't feed back synchronously (which triggers the noisy "ResizeObserver loop"
  // warning). A single observer for the component's lifetime avoids churn.
  useEffect(() => {
    const col = skillColumnRef.current;
    if (!col) return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => clipSkillRowsToColumn(col));
    });
    ro.observe(col);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  const sectionHeading = (title: ReactNode): ReactNode => (
    <div
      className="text-[13px] uppercase tracking-[0.12em] font-semibold pb-1 mb-3"
      style={{
        color: tokens.colors.sectionTitle,
        borderBottom: `2px solid ${tokens.colors.accent}`,
        breakAfter: "avoid",
        pageBreakAfter: "avoid",
      }}
    >
      {title}
    </div>
  );

  const renderSkills = () => {
    // Layout (cloud/list) takes precedence; SkillSummary stays only as a
    // backward-compat fallback for legacy sortedMode links.
    if (!vm.skills.displayAsList && vm.skills.sortedMode !== "" && vm.skills.skillCategoryBlocks) {
      return (
        <SkillSummary
          skillCategoryBlocks={vm.skills.skillCategoryBlocks}
          displayIcons={vm.skills.displayIcons}
          pdfMode={pdfMode}
          onRemoveSkill={onRemoveSkill}
        />
      );
    }
    return (
      <SkillsBlock
        skillCategoryBlocks={vm.skills.skillCategoryBlocks}
        editable={false}
        hideIcons={!vm.skills.displayIcons}
        pdfMode={pdfMode}
        onRemoveSkill={onRemoveSkill}
        onAddSkill={onAddStackSkill}
        asList={vm.skills.displayAsList}
        order={vm.skills.skillOrder}
        skillCounts={vm.skills.skillCounts}
      />
    );
  };

  const showPitch =
    !vm.personalInfo.hidePitch && (vm.personalInfo.editedPitch || vm.personalInfo.pitch) !== "";

  return (
    <div style={{ backgroundColor: tokens.colors.background }}>
      {showBranding && <ProfileBrandingHeader branding={vm.branding} tokens={tokens} />}
      {/* INTRO — two columns starting on the first page. No break-inside:avoid:
          a tall intro (long skill list) would otherwise be pushed wholesale to
          page 2, leaving page 1 empty. Letting it flow keeps it on page 1. */}
      <div className="flex flex-row gap-8 mb-10">
        {/* LEFT: photo + skills. Capped to ~one A4 page (A4 height minus the page
            margins and the contact header) with overflow hidden, so a long skill
            list never spills onto page 2. */}
        <div
          ref={skillColumnRef}
          className="w-[34%] flex flex-col gap-6 overflow-hidden"
          style={{ maxHeight: "227mm" }}
        >
          {vm.personalInfo.useProfilePicture && (
            <ProfilePhoto
              id="profile"
              allowEdit={false}
              isLoading={false}
              photoAssetId={vm.personalInfo.profilePhotoId}
              round={tokens.photo === "round"}
              useBorder={false}
              onImageSelected={() => {}}
              onExistingImageSelected={() => {}}
            />
          )}
          {vm.skills.skillTagsExist && (
            <div>
              {sectionHeading(vm.sectionTitles.skill_stack)}
              {renderSkills()}
            </div>
          )}
        </div>

        {/* RIGHT: identity + summary */}
        <div className="flex-1">
          <div className="flex flex-row items-start justify-between">
            <div className="flex-1">
              {onEditName ? (
                <DynamicInput
                  className="text-2xl font-bold leading-tight"
                  inputClassName="text-2xl font-bold leading-tight"
                  disabled={false}
                  label="Full name"
                  value={vm.personalInfo.editedName || vm.personalInfo.name}
                  validate={isString}
                  rows={1}
                  editable={true}
                  onBlur={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    onEditName(event.target.value)
                  }
                  type="textarea"
                />
              ) : (
                <h1
                  className="text-2xl font-bold leading-tight"
                  style={{ color: tokens.colors.text }}
                >
                  {vm.personalInfo.editedName || vm.personalInfo.name}
                </h1>
              )}
              {onEditTitle ? (
                <DynamicInput
                  className="text-base"
                  inputClassName="text-base"
                  disabled={false}
                  label="Title"
                  value={vm.personalInfo.editedTitle || vm.personalInfo.title}
                  validate={isString}
                  rows={1}
                  editable={true}
                  onBlur={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    onEditTitle(event.target.value)
                  }
                  type="textarea"
                />
              ) : (
                <div className="text-base" style={{ color: tokens.colors.muted }}>
                  {vm.personalInfo.editedTitle || vm.personalInfo.title}
                </div>
              )}
            </div>
            {logoUrl && (
              <img
                alt="logo"
                src={logoUrl}
                className="object-scale-contain ml-4"
                style={{ maxHeight: 50 }}
              />
            )}
          </div>

          {!vm.personalInfo.hidePitch &&
            (onEditPitch ? (
              <div className="mt-3">
                <DynamicInput
                  className="text-sm whitespace-pre-line transition-all duration-300 overflow-hidden"
                  inputClassName="text-sm"
                  disabled={false}
                  label="Describe yourself, your profession, your passion or anything you would like to share with potential colleagues"
                  value={vm.personalInfo.editedPitch || vm.personalInfo.pitch}
                  validate={isString}
                  rows={1}
                  editable={true}
                  onBlur={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    onEditPitch(event.target.value)
                  }
                  type="textarea"
                />
              </div>
            ) : showPitch ? (
              <div
                className="text-sm whitespace-pre-line mt-3"
                style={{ color: tokens.colors.text }}
              >
                {vm.personalInfo.editedPitch || vm.personalInfo.pitch}
              </div>
            ) : null)}

          {vm.linkConfig && vm.showProfileSummary && (
            <div className="mt-5">
              {sectionHeading(vm.sectionTitles.profile_summary)}
              <ProfileSummaryContent
                linkConfigData={vm.linkConfig}
                pdfMode={pdfMode}
                flat={true}
                onEditIntro={onEditIntro}
                onEditHighlight={onEditHighlight}
                onAddTopSkill={onAddTopSkill}
                onRemoveTopSkill={onRemoveTopSkill}
              />
            </div>
          )}
        </div>
      </div>

      {/* BODY — single full-width column, flows across pages */}
      {statsigClient?.checkGate("highlighted_projects") && vm.highlightedProjects.length > 0 && (
        <div className="mb-10">
          {sectionHeading(vm.sectionTitles.highlighted_projects)}
          <div className="flex flex-col gap-8 pl-3">
            {vm.highlightedProjects.map((highlightedProject) => (
              <div
                key={highlightedProject.title}
                style={{ breakInside: "avoid", pageBreakInside: "avoid" }}
              >
                <ProfileWorkplace
                  id={highlightedProject.title}
                  isEditable={false}
                  name={
                    highlightedProject.workplaceId
                      ? vm.getWorkplaceName(highlightedProject.workplaceId ?? "")
                      : highlightedProject.projectId
                        ? vm.getWorkplaceNameFromProject(highlightedProject.projectId)
                        : ""
                  }
                  skillTags={highlightedProject.skillTags ?? []}
                  role={highlightedProject.title ?? ""}
                  description={highlightedProject.summary}
                  isProject={true}
                  showSkills={vm.linkConfig?.highlightedProjectsConfig?.showSkills ?? false}
                  showSkillIcons={vm.linkConfig?.highlightedProjectsConfig?.showIcons ?? false}
                  showDescription={true}
                  onWorkplaceEdit={(_description: string) => {}}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {vm.linkConfig?.profileLinkWorkplaceIds &&
        vm.linkConfig.profileLinkWorkplaceIds.length > 0 && (
          <div className="mb-10 pl-3">
            {vm.workplaces.map((workplace, index) => (
              <div
                className="mb-8"
                key={workplace.id}
                style={{
                  backgroundColor: "#fff",
                  ...(vm.avoidWorkplacePageBreak && vm.avoidWorkplaceDescriptionPageBreak
                    ? { breakInside: "avoid", pageBreakInside: "avoid" }
                    : {}),
                }}
              >
                {index === 0 && sectionHeading(vm.sectionTitles.work_experience)}
                <ProfileWorkplace
                  id={workplace.id}
                  isEditable={isEditable}
                  fieldsEditable={!!onEditWorkplace}
                  name={workplace.name}
                  skillTags={workplace.skillTags}
                  role={workplace.role}
                  pdfMode={pdfMode}
                  description={workplace.description}
                  startDate={workplace.startDate}
                  endDate={workplace.endDate}
                  showSkills={vm.linkConfig?.workplaceLinkConfig?.displaySkills}
                  showSkillIcons={vm.linkConfig?.workplaceLinkConfig?.displaySkillIcons ?? true}
                  showDescription={vm.linkConfig?.workplaceLinkConfig?.displayDescription}
                  avoidPageBreak={vm.avoidWorkplacePageBreak}
                  onWorkplaceEdit={(description) =>
                    onEditWorkplace?.(workplace.id, { description })
                  }
                  onWorkplaceContentUpdate={(_id, role, _description, name) =>
                    onEditWorkplace?.(workplace.id, { role, name })
                  }
                  onProjectClickAction={() => {}}
                />
              </div>
            ))}
          </div>
        )}

      {vm.linkConfig?.profileLinkProjects && vm.linkConfig.profileLinkProjects.length > 0 && (
        <div className="mb-10 pl-3">
          {vm.projects.map((project, index) => (
            <div className="mb-8" key={project.id} style={{ backgroundColor: "#fff" }}>
              {index === 0 && sectionHeading(vm.sectionTitles.projects)}
              <ProfileProject
                id={project.id}
                isEditable={false}
                name={
                  project.isSideProject
                    ? "Side project"
                    : vm.getWorkplaceName(project.workplaceId ?? "")
                }
                skillTags={project.skillTags}
                title={project.title}
                description={project.pitch}
                showSkills={vm.linkConfig?.projectsLinkConfig?.displaySkills}
                showDescription={true}
                showSkillIcons={vm.linkConfig?.projectsLinkConfig?.displaySkillIcons ?? true}
                onWorkplaceEdit={() => {}}
                onWorkplaceContentUpdate={() => {}}
                onProjectClickAction={() => {}}
                onWorkplaceNameClick={() => {}}
                profileId={vm.accountProfileId}
                pdfMode={pdfMode}
              />
            </div>
          ))}
        </div>
      )}

      {vm.showEducationSection && vm.educations.length > 0 && (
        <div className="mb-10">
          {sectionHeading(vm.sectionTitles.education)}
          <div className="pl-3">
            {vm.educations.map((education, index) => (
              <div className="mb-6" key={education.id}>
                <ProfileEducation
                  isEditable={false}
                  isDarkModeActive={false}
                  key={index}
                  isBeingEdited={false}
                  isNew={false}
                  education={education}
                  onSaveItem={() => {}}
                  onCancelEdit={() => {}}
                  onRemove={() => {}}
                  onShowDialog={() => {}}
                  isLastInOrder={false}
                  onContentImprovementStarted={() => {}}
                  onContentImprovementEnded={() => {}}
                  pdfMode={false}
                  removePadding={true}
                  staticMode={true}
                  onProjectClickAction={() => {}}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const splitTemplate: CVTemplate = {
  id: "split",
  name: "Split",
  Component: SplitTemplate,
};

export default SplitTemplate;
